import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { listDeclarations, uploadJustification } from '../services/assujettiService';

const statusStyles: Record<string, { label: string; cls: string }> = {
  submitted: { label: 'Soumise', cls: 'bg-blue-100 text-blue-700' },
  draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-700' },
  validated: { label: 'Validee', cls: 'bg-green-100 text-green-700' },
  paid: { label: 'Payee', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejetee', cls: 'bg-red-100 text-red-700' },
};

function badge(status: string) {
  const s = statusStyles[status?.toLowerCase()] || { label: status || '--', cls: 'bg-gray-100 text-gray-600' };
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.cls}`}>{s.label}</span>;
}

export default function V4DeclarationsPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadTarget, setUploadTarget] = useState<string | null>(null);

  function triggerUpload(declarationId: string) {
    setUploadTarget(declarationId);
    fileRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    setUploadMsg(null);
    try {
      await uploadJustification(uploadTarget, file);
      setUploadMsg('Justification envoyee.');
    } catch (err: any) {
      setUploadMsg('Erreur: ' + (err.message || 'echec'));
    }
    if (fileRef.current) fileRef.current.value = '';
    setUploadTarget(null);
  }

  useEffect(() => {
    (async () => {
      try {
        const data = await listDeclarations();
        setRows(Array.isArray(data) ? data : data?.declarations ?? []);
      } catch (err: any) {
        setError(err.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8 text-center text-gray-500">Chargement...</div>;
  if (error) return <div className="p-8 text-center text-red-600">{error}</div>;

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Declarations</h3>

        {rows.length === 0 ? (
          <p className="text-gray-400 text-sm">Aucune declaration trouvee</p>
        ) : (
          <div className="overflow-x-auto">
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} className="hidden" />
            {uploadMsg && <div className={`text-sm rounded-lg p-2 mb-3 ${uploadMsg.startsWith('Erreur') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{uploadMsg}</div>}
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="border-b text-left text-xs text-gray-500 uppercase">
                  <th className="py-3 px-3">Periode</th>
                  <th className="py-3 px-3">Etablissement</th>
                  <th className="py-3 px-3">USD</th>
                  <th className="py-3 px-3">CDF</th>
                  <th className="py-3 px-3">Ecart</th>
                  <th className="py-3 px-3">Statut</th>
                  <th className="py-3 px-3">Justif.</th>
                  <th className="py-3 px-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: any, i: number) => (
                  <tr key={r.id || i} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-3">{r.period || '--'}</td>
                    <td className="py-3 px-3">{r.establishment_name || '--'}</td>
                    <td className="py-3 px-3">{r.amount_usd != null ? `${Number(r.amount_usd).toLocaleString()} USD` : '--'}</td>
                    <td className="py-3 px-3">{r.amount_cdf != null ? `${Number(r.amount_cdf).toLocaleString()} CDF` : '--'}</td>
                    <td className="py-3 px-3">{r.variance_pct != null ? `${r.variance_pct} %` : '--'}</td>
                    <td className="py-3 px-3">{badge(r.status)}</td>
                    <td className="py-3 px-3">
                      <button onClick={() => r.id && triggerUpload(r.id)} title="Joindre justification" className="border px-2 py-1 rounded hover:bg-gray-100 text-xs">Joindre</button>
                    </td>
                    <td className="py-3 px-3">
                      <Link to={`/fpt/v4/dossier?id=${r.id || ''}`} className="border px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-gray-100">Ouvrir</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
